// WHICH LEVEL A TRACKING PAGE FOLLOWS (owner, 2026-10-26: "make each
// tracking page follow its level's sheet").
//
// Picked here, on the page bar: the page then shows that level's drawing
// from Levels, Areas & Systems - and keeps showing it when the drawing is
// changed there (back/utils/trackingPageLevels) - and its own sheet
// pickers are locked. "Its own drawing" unlinks it.
//
// Its own component, not more JSX in ModelTracking: that file is big enough
// that every extra hook in it trips the rules-of-hooks path limit.
import React, { useEffect, useState } from "react";
import { Select, Tooltip, message } from "antd";
import { listLevels } from "api/areas/areasApi";
import { updatePage } from "api/tracking/trackingElementsApi";
import { popupInStage } from "./stagePopup";

const OWN = "__own__";

// The levels a page can follow: saved ones only (a derived "virtual:N" level
// is not a row a page can point at), and the one it follows even if it has
// gone, so the picker never shows a blank.
export function levelOptions(levels, page) {
  const out = (levels || [])
    .filter((l) => l && l._id && !String(l._id).startsWith("virtual:"))
    .map((l) => ({
      value: String(l._id),
      label: l.sheetDocControlId ? `Follows ${l.name}` : `Follows ${l.name} (no drawing yet)`,
    }));
  const cur = page && page.levelPageId ? String(page.levelPageId) : "";
  if (cur && !out.some((o) => o.value === cur)) out.push({ value: cur, label: "Follows a removed level" });
  return [{ value: OWN, label: "Its own drawing" }, ...out];
}

export default function PageLevelLink({ projectId, page, canEdit = false, onSaved }) {
  const [levels, setLevels] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    if (!projectId) return undefined;
    listLevels(projectId)
      .then((r) => { if (alive) setLevels((r && r.pages) || []); })
      .catch(() => { if (alive) setLevels([]); });
    return () => { alive = false; };
  }, [projectId]);
  if (!page) return null;
  const value = page.levelPageId ? String(page.levelPageId) : OWN;
  return (
    <Tooltip getPopupContainer={popupInStage}
      title="Follow a level and this page shows that level's drawing from Levels, Areas & Systems, and keeps showing it when the drawing is changed there. Each drawing keeps its own placement on the model.">
      <Select getPopupContainer={popupInStage}
        size="small"
        style={{ minWidth: 170 }}
        value={value}
        loading={busy}
        disabled={!canEdit || busy}
        options={levelOptions(levels, page)}
        onChange={async (v) => {
          setBusy(true);
          try {
            const r = await updatePage(projectId, page._id, { levelPageId: v === OWN ? null : v });
            if (onSaved) onSaved(r.page);
            if (v !== OWN) {
              const placed = r.page && r.page.sheet && (Number(r.page.sheet.scale) !== 100 || Number(r.page.sheet.rotation) || Number((r.page.sheet.position || {}).x) || Number((r.page.sheet.position || {}).z));
              if (!placed) message.info("This drawing is not placed on the model yet. Place it in View Config (Align by 2 points), so its areas tag the model.", 8);
            }
          } catch (e) {
            message.error(e.message || "Could not link the page to that level");
          } finally {
            setBusy(false);
          }
        }}
      />
    </Tooltip>
  );
}
